import { CountryCode, PRIVATE_PRODUCT_ERROR_CODES, THIRD_PARTY_ORDER_ERROR_CODES, type ProjectCode } from '@constants'
import { useXPlatformInfo } from '@cosign-services/hooks/useXPlatformInfo.ts'
import { useApiECommerceCartCalculate } from '@hooks-api/eCommerceV2/useApiECommerceCartCalculate'
import { type PrivateProductErrorDetail, type CartCalculateErrorResponse, type NoDataErrorDetail } from '@hooks-api/types/apiErrorResponse'
import { type ECommerceCartCalculateRequest } from '@hooks-api/types/apiRequest'
import { getWordPressLocale } from '@utils/getWordPressLocale'
import { type AxiosError } from 'axios'
import { useEffect, useMemo, useRef } from 'react'
import * as couponService from '../core/couponService'
import { type WpCartValues } from '../types'
import { useWpCartMemorySelector } from './useWpCartMemoryStore'
import { useWpCartProductsInfo } from './useWpCartProductsInfo'
import { useWpCartSelector, useWpCartSetter, getWpCartValues, useWpCartAllItems, useWpCartAllSpecificAddonItems } from './useWpCartStore'
import { useWpSyncCartSessionToStore } from './useWpSyncCartSessionToStore'

export const useWpCartCalculate = (
  projectCode: ProjectCode,
  countryCode: CountryCode,
  shouldCalculateProp = true,
) => {
  const { isUserLoggedIn, isXPlatformInfoLoading } = useXPlatformInfo()
  const { hasSyncCartSession } = useWpSyncCartSessionToStore({ isXPlatformInfoLoading, isUserLoggedIn, countryCode })
  const shouldCalculate = shouldCalculateProp && hasSyncCartSession
  const cartItems = useWpCartAllItems(countryCode)
  const billingCountryFromStore = useWpCartSelector(countryCode, state => state.billing.billingCountry)
  const billingAddressPostcode = useWpCartSelector(countryCode, state => state.billing.billingAddressPostcode)
  const billingAddressState = useWpCartSelector(countryCode, state => state.billing.billingAddressState)
  const selectedManualCouponId = useWpCartSelector(countryCode, state => state.coupon.selectedManualCouponId)
  const selectedClaimCouponRedeemCode = useWpCartSelector(countryCode, state => state.coupon.selectedClaimCouponRedeemCode)
  const selectedExchangeVoucherRedeemCodes = useWpCartSelector(countryCode, state => state.coupon.selectedExchangeVoucherRedeemCodes)
  const appliedShippingMethodId = useWpCartSelector(countryCode, state => state.shipping.appliedShippingMethodId)
  const isUserAppliedRewardPoints = useWpCartSelector(countryCode, state => state.rewardPoints.isUserAppliedRewardPoints)
  const userInputRewardPoints = useWpCartSelector(countryCode, state => state.rewardPoints.userInputRewardPoints)
  const specificAddonItems = useWpCartAllSpecificAddonItems(countryCode)
  const creditCardAffiliateCode = useWpCartMemorySelector(countryCode, state => state.payment.creditCardAffiliateCode)
  const setCartValues = useWpCartSetter(countryCode)

  const language = getWordPressLocale()

  /**
   * WP 後台移除 variation 後再新建同 SKU 的 variation，舊的 variation_id（cartItemId）會變成無效，
   * 導致 /products API 查不到資料，進而使購物車渲染失敗，但是 calculate 仍然可以正常執行。
   * 複用 useWpCartProductsInfo 的 productMap（共用 SWR cache，不額外打 API），
   * 比對 cartItems 的 SKU 是否存在於 productMap 中，若有無效商品則自動從 store 移除。
   */
  const { productMap, isProductsValidating, isProductIdsStale } = useWpCartProductsInfo(projectCode, countryCode)
  const hasRemovedInvalidItems = useRef(false)

  useEffect(() => {
    function removeInvalidCartItems() {
      if (!productMap || cartItems.length === 0) return
      if (isProductIdsStale || isProductsValidating) return

      const validSkus = new Set(
        Object.values(productMap)
          .map(product => product?.sku)
          .filter(Boolean),
      )
      const invalidItems = cartItems.filter(item => item.quantity > 0 && !validSkus.has(item.sku))

      if (invalidItems.length === 0) {
        hasRemovedInvalidItems.current = false
        return
      }

      if (hasRemovedInvalidItems.current) return
      hasRemovedInvalidItems.current = true

      console.warn('[useWpCartCalculate] removing invalid cart items with SKUs not found in /products:', {
        invalidItems: invalidItems.map(item => ({ cartItemId: item.cartItemId, sku: item.sku })),
      })

      const invalidCartItemIds = new Set(invalidItems.map(item => item.cartItemId))

      setCartValues(state => ({
        ...state,
        cart: {
          ...state.cart,
          items: state.cart.items.filter(item => !invalidCartItemIds.has(item.cartItemId)),
          addonItems: (state.cart.addonItems || []).filter(item => !invalidCartItemIds.has(item.cartItemId)),
        },
      }))
    }
    removeInvalidCartItems()
  }, [productMap, cartItems, setCartValues, isProductIdsStale, isProductsValidating])

  /**
   * 美國站需要帶入 billing_address_state 和 billing_address_postcode，其他國家不帶入
   */
  const shouldSendRequestStateAndPostcode = countryCode === CountryCode.US

  const handleCalculateError = (err: AxiosError<CartCalculateErrorResponse>) => {
    /**
     * 防止美國站郵遞區號和州別 retry，導致無窮迴圈
     */
    const invalidParameters = err.response?.data.error?.code
    const errorDetails = err.response?.data.error.detail
    // 檢查 errorDetails 是否為陣列
    let hasPostcodeError = false
    let hasStateError = false
    if (Array.isArray(errorDetails)) {
      hasPostcodeError = errorDetails?.some(detail => Object.keys(detail).includes('billing_address_postcode'))
      hasStateError = errorDetails?.some(detail => Object.keys(detail).includes('billing_address_state'))
    }

    if (invalidParameters === 5300 && (hasPostcodeError || hasStateError)) {
      setCartValues(state => ({
        ...state,
        billing: {
          ...state.billing,
          billingAddressPostcode: '',
          billingAddressState: '',
        },
      }))
      return
    }

    /**
     * 處理 404 錯誤 - 商品移除 自動移除商品
     */
    if (err.status === 404 && invalidParameters === THIRD_PARTY_ORDER_ERROR_CODES.NO_DATA) {
      const errorDetail = err.response?.data?.error?.detail as NoDataErrorDetail
      const sku = errorDetail?.sku
      if (sku) {
        setCartValues(state => ({
          ...state,
          cart: {
            ...state.cart,
            items: state.cart.items.filter(item => item.sku !== sku),
            addonItems: (state.cart.addonItems || []).filter(item => item.sku !== sku),
          },
        }))
      }
      return
    }

    /**
     * 處理 404 錯誤 - 私密商品錯誤 自動移除私密商品
     */
    if (err.status === 404 && invalidParameters === PRIVATE_PRODUCT_ERROR_CODES.PRIVATE_PRODUCT) {
      const errorDetail = err.response?.data?.error?.detail as PrivateProductErrorDetail
      const cartItemId = errorDetail?.product_id
      if (cartItemId) {
        setCartValues(state => ({
          ...state,
          cart: {
            ...state.cart,
            items: state.cart.items.filter(item => item.cartItemId !== cartItemId),
            addonItems: (state.cart.addonItems || []).filter(item => item.cartItemId !== cartItemId),
          },
        }))

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('privateProductRemoved'))
        }
      }
      return
    }
  }

  const calculateCartItems = useMemo(() => {
    const calculateCartItems = cartItems
      .filter(cartItem => cartItem.quantity > 0).map(cartItem => ({
        sku: cartItem.sku,
        project_code: cartItem.project_code,
        quantity: cartItem.quantity,
        is_addon: !!cartItem.is_addon,
        is_addon_v2: !!cartItem.is_addon_v2,
        subship_info: cartItem.subship_info,
        addon_setting_id: cartItem.addon_setting_id ?? null,
        addon_main_product_id: null,
      }))
    const calculateSpecificAddonItems = specificAddonItems
      .filter(specificAddonItem => specificAddonItem.quantity > 0).map(specificAddonItem => ({
        sku: specificAddonItem.sku,
        project_code: specificAddonItem.project_code,
        quantity: specificAddonItem.quantity,
        is_addon: !!specificAddonItem.is_addon,
        is_addon_v2: !!specificAddonItem.is_addon_v2,
        subship_info: specificAddonItem.subship_info,
        addon_setting_id: specificAddonItem.addon_setting_id ?? null,
        addon_main_product_id: specificAddonItem.addon_main_product_id ?? null,
      }))
    return [...calculateCartItems, ...calculateSpecificAddonItems]
  }, [cartItems, specificAddonItems])

  // 需要用 useMemo 來避免每次 render 時 value 的 reference 變更，否則會導致無窮迴圈
  const value = useMemo(() => {
    const cartValues = getWpCartValues(countryCode)
    // 一般優惠券（單選）+ 點數兌換券（多選）合併送出，去除空值與重複。
    const redeemCodes = couponService.mergeRedeemCodes(selectedExchangeVoucherRedeemCodes, selectedClaimCouponRedeemCode)
    const request: ECommerceCartCalculateRequest<WpCartValues> = {
      billing_country: billingCountryFromStore,
      project_code: projectCode,
      country_code: countryCode,
      order_items: calculateCartItems,
      manual_input_coupon_ids: selectedManualCouponId ? [selectedManualCouponId] : undefined,
      redeem_codes: redeemCodes.length > 0 ? redeemCodes : undefined,
      applied_shipping_method_id: appliedShippingMethodId,
      reward_point: isUserAppliedRewardPoints ? userInputRewardPoints : undefined,
      billing_address_state: shouldSendRequestStateAndPostcode ? billingAddressState : undefined,
      billing_address_postcode: shouldSendRequestStateAndPostcode ? billingAddressPostcode : undefined,
      credit_card_affiliate_code: creditCardAffiliateCode || undefined,
      language,
      cart_values: cartValues,
    }
    return request
  }, [appliedShippingMethodId, billingAddressPostcode, billingAddressState, billingCountryFromStore, calculateCartItems, countryCode, creditCardAffiliateCode, isUserAppliedRewardPoints, language, projectCode, selectedClaimCouponRedeemCode, selectedExchangeVoucherRedeemCodes, selectedManualCouponId, shouldSendRequestStateAndPostcode, userInputRewardPoints])

  const { destructuredData: calculateResult, mutate, isLoading, isValidating, error } = useApiECommerceCartCalculate(shouldCalculate ? value : null, {
    keepPreviousData: true,
    // 購物車內容變動會經 store 同步改變 key 而重算，focus 時不需再打一次 calculate
    revalidateOnFocus: false,
    onError: handleCalculateError,
  })

  // 此 hook 無 debounce 層（不像 b2b/campaign 用 useDebounceValue），所以只看 isValidating 即可
  const isStale = isValidating

  return {
    error,
    calculateResult,
    mutate,
    isLoading,
    isStale,
    calculateValue: value,
  }
}
